/// <reference types="vite-plugin-pwa/client" />
import { useState, useEffect, useCallback } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { consumeDbVersionErrorOverride } from '@/lib/pwa-update-flag';

declare const __BUILD_TIME__: string;

const DISMISS_STORAGE_KEY = 'pwa-update-dismissed';
const DISMISS_DURATION_MS = 2 * 60 * 60 * 1000; // 2 hours
const CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

function wasDismissedRecently(): boolean {
  try {
    const dismissed = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!dismissed) return false;
    return Date.now() - parseInt(dismissed, 10) < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

// Module-level singleton — registerSW must only be called once per page load
type Listener = (hasUpdate: boolean) => void;
const listeners = new Set<Listener>();
let sharedHasUpdate = false;
let sharedRegistration: ServiceWorkerRegistration | null = null;
let initialized = false;

function notifyListeners() {
  listeners.forEach((fn) => fn(sharedHasUpdate));
}

function initPWAUpdate() {
  if (initialized) return;
  initialized = true;

  console.info('[PWA] build', __BUILD_TIME__);

  // Reload once when a new SW takes control of the page
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  registerSW({
    onNeedRefresh() {
      sharedHasUpdate = true;
      notifyListeners();
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      sharedRegistration = registration;

      // Periodically check for updates
      setInterval(() => {
        registration.update().catch(() => {});
      }, CHECK_INTERVAL_MS);

      // Check for updates when user returns to the tab
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          registration.update().catch(() => {});
        }
      });
    },
  });
}

export function usePWAUpdate() {
  const [hasUpdate, setHasUpdate] = useState(sharedHasUpdate);
  const [dismissed, setDismissed] = useState(() => wasDismissedRecently());
  // Appendix B: a db VersionError self-heal (db.ts handleDbOpenError) raised a
  // one-shot override flag because the session is db-less. Consume it into
  // state so this session's refresh prompt bypasses the dismissal snooze —
  // keyed on hasUpdate so a flag raised at startup is still picked up once the
  // SW update actually arrives. Cleared on capture, so future normal snoozes
  // behave normally.
  const [dbVersionOverride, setDbVersionOverride] = useState(false);

  useEffect(() => {
    if (!dbVersionOverride && consumeDbVersionErrorOverride()) {
      setDbVersionOverride(true);
    }
  }, [hasUpdate, dbVersionOverride]);

  useEffect(() => {
    initPWAUpdate();

    const listener: Listener = (value) => setHasUpdate(value);
    listeners.add(listener);

    // Sync with current shared state in case update happened before mount
    setHasUpdate(sharedHasUpdate);

    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Re-evaluate dismissal on visibility change (for long-lived tabs)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !wasDismissedRecently()) {
        setDismissed(false);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const update = useCallback(() => {
    // Send SKIP_WAITING to the waiting SW; our controllerchange listener reloads the page
    sharedRegistration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
    } catch {
      // localStorage unavailable
    }
  }, []);

  return {
    hasUpdate: hasUpdate && (!dismissed || dbVersionOverride),
    update,
    dismiss,
  };
}
